import { DEFAULT_CURRENCY } from '@app/common/constants';
import Text from '@app/components/ui/Text/Text';
import { getDayjsLocaleByLanguage, getNumberFormattingLocaleByLanguage } from '@app/i18n/helpers';
import { SupportedCurrencies } from '@app/utils/pricingCurrencyUtils';
import { Card } from 'antd';
import dayjs from 'dayjs';
import 'dayjs/locale/el';
import { useTranslation } from 'react-i18next';
import IncomeVsExpensesChart from './IncomeVsExpensesChart';
import IncomeVsExpensesTable from './IncomeVsExpensesTable';
import { FinanceIncomeExpensesFormattedRow, FinanceIncomeExpensesPeriod, FinanceIncomeExpensesRow } from './types';

type FinanceIncomeExpensesProps = {
  data: FinanceIncomeExpensesRow[];
  period?: FinanceIncomeExpensesPeriod;
  customRangeLabel?: string;
  currency?: SupportedCurrencies;
  isLoading?: boolean;
};

const FinanceIncomeExpenses = ({
  data,
  period = 'this-month',
  customRangeLabel,
  currency = DEFAULT_CURRENCY,
  isLoading = false,
}: FinanceIncomeExpensesProps) => {
  const { t, i18n } = useTranslation();
  const locale = getNumberFormattingLocaleByLanguage(i18n.language);
  const dayjsLocale = getDayjsLocaleByLanguage(i18n.language);

  const monthDates = data.map((row) => row.month).filter((month) => dayjs.isDayjs(month));

  const formattedRows: FinanceIncomeExpensesFormattedRow[] = data.map((row) => ({
    ...row,
    monthLabel: dayjs.isDayjs(row.month) ? row.month.locale(dayjsLocale).format('MMMM') : row.month,
  }));

  const monthYears = monthDates.map((date) => date.year());
  const minYear = monthYears.length > 0 ? Math.min(...monthYears) : undefined;
  const maxYear = monthYears.length > 0 ? Math.max(...monthYears) : undefined;
  const yearLabel = minYear && maxYear ? (minYear === maxYear ? `${minYear}` : `${minYear}-${maxYear}`) : '';
  const monthHeader = yearLabel ? `${t('month-capital')} (${yearLabel})` : t('month-capital');

  const periodLabel = period === 'custom' ? (customRangeLabel ?? t('period-custom')) : t(`period-${period}`);

  return (
    <Card
      loading={isLoading}
      title={
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <Text type="label1" transKey="income-vs-expenses" />

          <div className="flex flex-col sm:items-end">
            <Text type="caption" transKey="period" color="#64748b" />

            <Text type="body2" untranslatedText={periodLabel} />
          </div>
        </div>
      }
      className="w-full"
      styles={{
        header: { borderBottom: '1px solid #eef2f7' },
        body: { padding: 0 },
      }}
    >
      <div className="px-6 pt-4 pb-2">
        <IncomeVsExpensesChart rows={formattedRows} locale={locale} />
      </div>

      <div className="border-t border-slate-100">
        <IncomeVsExpensesTable rows={formattedRows} currency={currency} locale={locale} monthHeader={monthHeader} />
      </div>
    </Card>
  );
};

export default FinanceIncomeExpenses;
